import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

function compareStudentIds(a, b) {
  return String(a?.student_code || '').localeCompare(
    String(b?.student_code || ''),
    undefined,
    {
      numeric: true,
      sensitivity: 'base'
    }
  );
}

function getPhotoUrl(fileId, version = 0) {
  if (!fileId) return '';
  return `https://drive.google.com/thumbnail?id=${encodeURIComponent(fileId)}&sz=w200&v=${version}`;
}

async function syncStudentPhotosFromDrive() {
  await fetch(API_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'syncStudentPhotos'
    })
  });
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = event => {
      try {
        const result = String(event.target?.result || '');
        const commaIndex = result.indexOf(',');

        if (commaIndex < 0) {
          reject(new Error('Could not read the selected image.'));
          return;
        }

        resolve(result.slice(commaIndex + 1));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () =>
      reject(new Error('Could not read the selected image.'));

    reader.readAsDataURL(file);
  });
}

async function fetchStudents(institutionId, isSuperadmin) {
  const filter = isSuperadmin
    ? ''
    : `&institution_id=eq.${institutionId}`;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students?select=*&order=student_code.asc${filter}`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  const data = await response.json();

return (Array.isArray(data) ? data : [])
  .sort(compareStudentIds);
}

async function saveStudents(rows) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json',
        'Prefer':
          'resolution=merge-duplicates,return=representation'
      },
      body: JSON.stringify(rows)
    }
  );

  const text =
    await response.text();

  let result = null;

  try {
    result = text
      ? JSON.parse(text)
      : null;
  } catch {
    throw new Error(
      `Student save returned an invalid response (${response.status}).`
    );
  }

  if (!response.ok) {
    throw new Error(
      result?.message ||
      result?.details ||
      result?.hint ||
      `Student save failed (${response.status}).`
    );
  }

  if (!Array.isArray(result)) {
    throw new Error(
      'Student save did not return the expected student records.'
    );
  }

  return result;
}

async function updateStudent(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

export default function StudentManagement({ session }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bulkFields, setBulkFields] = useState({
    chinese: '', code: '', english: '', email: ''
  });
  const [parseErrors, setParseErrors] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [photoLoading, setPhotoLoading] = useState({});
  const [photoSyncing, setPhotoSyncing] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [brokenPhotos, setBrokenPhotos] = useState({});
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
  let cancelled = false;

  async function loadStudents() {
    setLoading(true);

    try {
      const initialStudents = await fetchStudents(
        session.institutionId,
        session.isSuperadmin
      );

      if (!cancelled) {
        setStudents(initialStudents);
      }
    } catch (err) {
      console.error('Student load failed:', err);

      if (!cancelled) {
        setError('Failed to load students.');
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  }

  loadStudents();

  return () => {
    cancelled = true;
  };
}, [session.institutionId, session.isSuperadmin]);

  async function handleBulkAdd(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setParseErrors([]);

    const chinese = bulkFields.chinese.split('\n').map(l => l.trim());
    const codes = bulkFields.code.split('\n').map(l => l.trim());
    const english = bulkFields.english.split('\n').map(l => l.trim());
    const emails = bulkFields.email.split('\n').map(l => l.trim());

    const count = Math.max(
  chinese.length,
  codes.length,
  english.length,
  emails.length
);
    const errors = [];
    const parsed = [];

    for (let i = 0; i < count; i++) {
      const chineseName = chinese[i] || '';
      const code = codes[i] || '';
      const englishName = english[i] || '';
      const email = emails[i] || '';

      if (
  !chineseName &&
  !englishName &&
  !code &&
  !email
) {
  continue;
}

if (
  !chineseName &&
  !englishName
) {
  errors.push(
    `Line ${i + 1}: Either Chinese name or English name is required.`
  );
  continue;
}

if (!code) {
  errors.push(
    `Line ${i + 1}: Student ID is required.`
  );
  continue;
}

      parsed.push({
  chinese_name: chineseName,
  student_code: code,
  english_name: englishName || null,
  email: email || null,
  active: true,
  institution_id: session.institutionId,
  added_by_instructor_id: session.instructorId
});
    }

    if (errors.length) { setParseErrors(errors); return; }
    if (!parsed.length) return setError('Enter at least one student.');

    setSaving(true);
    try {
      const result = await saveStudents(parsed);
      setStudents(prev => {
        const ids = new Set(result.map(s => s.id));
        return [...prev.filter(s => !ids.has(s.id)), ...result]
          .sort(compareStudentIds);
      });
      setBulkFields({ chinese: '', code: '', english: '', email: '' });
      setSuccess(`${result.length} student${result.length !== 1 ? 's' : ''} added or updated.`);
    } catch (err) {
  console.error(
    'Student save failed:',
    err
  );

  setError(
    err?.message ||
    'Failed to save students.'
  );
} finally {
      setSaving(false);
    }
  }

async function handleUpdateStudent(id, data) {
  const result = await updateStudent(id, data);

  setStudents(prev =>
    prev
      .map(s =>
        s.id === id ? result[0] : s
      )
      .sort(compareStudentIds)
  );
}

  async function handleArchiveStudent(id, active) {
    const result = await updateStudent(id, { active });
    setStudents(prev => prev.map(s => s.id === id ? result[0] : s));
  }

  async function refreshPhotosFromDrive(showMessage = false) {
    setPhotoSyncing(true);
    setError('');

    try {
      await syncStudentPhotosFromDrive();

      const refreshedStudents = await fetchStudents(session.institutionId, session.isSuperadmin);
      setStudents(refreshedStudents);
      setBrokenPhotos({});
      setPhotoVersion(version => version + 1);

      if (showMessage) {
        setSuccess('Student photos refreshed from the shared Drive folder.');
      }
    } catch (err) {
      console.error('Student photo refresh failed:', err);
      setError('Failed to refresh student photos.');
    } finally {
      setPhotoSyncing(false);
    }
  }

  async function handlePhotoUpload(studentCode, file) {
    if (!file) return;

    setPhotoLoading(prev => ({
      ...prev,
      [studentCode]: true
    }));
    setError('');
    setSuccess('');

    try {
      const base64Data = await readFileAsBase64(file);

      await fetch(API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'uploadStudentPhoto',
          studentCode,
          base64Data,
          mimeType: file.type || 'image/jpeg'
        })
      });

      // The Apps Script writes the Drive ID back to Supabase.
      // Re-read the student rows so the new photo displays immediately.
      const refreshedStudents = await fetchStudents(session.institutionId, session.isSuperadmin);
      setStudents(refreshedStudents);
      setBrokenPhotos(prev => {
        const next = { ...prev };
        delete next[studentCode];
        return next;
      });
      setPhotoVersion(version => version + 1);
      setSuccess('Photo uploaded.');
    } catch (err) {
      console.error('Student photo upload failed:', err);
      setError('Failed to upload photo.');
    } finally {
      setPhotoLoading(prev => ({
        ...prev,
        [studentCode]: false
      }));
    }
  }

async function handleCreateMaster() {
  if (!session.isSuperadmin) {
    setError('Only the superadmin can create a Master Student account.');
    return;
  }

  setSaving(true);
  setError('');

  try {
    const result = await saveStudents([{
      chinese_name: 'Master Account',
      student_code: 'MASTER',
      english_name: 'Master Account',
      email: null,
      active: true,
      is_master: true,
      is_honors: false
    }]);

    setStudents(prev =>
  [...prev, result[0]]
    .sort(compareStudentIds)
);

    setSuccess(
      'Master student account created.'
    );
  } catch (err) {
    setError(
      'Failed to create master account.'
    );
  } finally {
    setSaving(false);
  }
}
  const activeStudents =
    students.filter(
      student =>
        student.active &&
        (
          session?.isSuperadmin === true ||
          !student.is_master
        )
    );
  const archivedStudents = students.filter(s => !s.active);

  const filtered = activeStudents.filter(s => {
    const term = search.toLowerCase();
    if (!term) return true;
    return [s.student_code, s.chinese_name, s.english_name, s.email]
      .filter(Boolean)
      .some(v => v.toLowerCase().includes(term));
  });

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h1 style={styles.heading}>Student Management</h1>
      <p style={styles.muted}>
        Add students to the system here. Once added, enrol them in courses
        via Course Management.
      </p>
{session.isSuperadmin && (
  <>
    {!students.some(s => s.is_master) && (
      <div style={styles.masterBox}>
        <div style={styles.masterText}>
          No master student account exists. Create one to enable Student View.
        </div>

        <button
          style={styles.btn}
          disabled={saving}
          onClick={handleCreateMaster}
        >
          Create Master Student Account
        </button>
      </div>
    )}

    {students.some(s => s.is_master) && (
      <div style={styles.masterBox}>
        <div style={styles.masterText}>
          ✓ Master student account exists —{' '}
          <strong>
            {
              students.find(
                s => s.is_master
              )?.student_code
            }
          </strong>
        </div>
      </div>
    )}
  </>
)}
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Bulk add form */}
      <div style={styles.card}>
        <h2 style={styles.subheading}>Add Students</h2>
        <p style={styles.muted}>
          Enter one student per line in each box. Lines are matched by position —
          line 1 in each box belongs to the same student. Student ID and either a Chinese or English name are required.
        </p>
        <form onSubmit={handleBulkAdd}>
          <div style={styles.bulkGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Chinese Name</label>
              <textarea
                style={styles.textarea}
                value={bulkFields.chinese}
                onChange={e => setBulkFields(prev => ({ ...prev, chinese: e.target.value }))}
                placeholder={'王小明\n李美華\n張偉'}
                rows={8}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Student ID (required)</label>
              <textarea
                style={styles.textarea}
                value={bulkFields.code}
                onChange={e => setBulkFields(prev => ({ ...prev, code: e.target.value }))}
                placeholder={'B11234\nB11235\nB11236'}
                rows={8}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>English Name</label>
              <textarea
                style={styles.textarea}
                value={bulkFields.english}
                onChange={e => setBulkFields(prev => ({ ...prev, english: e.target.value }))}
                placeholder={'Ming Wang\nMeihua Li\n'}
                rows={8}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email (optional)</label>
              <textarea
                style={styles.textarea}
                value={bulkFields.email}
                onChange={e => setBulkFields(prev => ({ ...prev, email: e.target.value }))}
                placeholder={'b11234@email.com\n\n'}
                rows={8}
              />
            </div>
          </div>

          {parseErrors.length > 0 && (
            <div style={styles.error}>
              {parseErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          <button style={styles.btn} disabled={saving}>
            {saving ? 'Saving...' : 'Add Students'}
          </button>
        </form>
      </div>

      {/* Search and list */}
      <div style={styles.card}>
        <div style={styles.listHeader}>
          <h2 style={styles.subheading}>
            All Students ({activeStudents.length})
          </h2>

          <div style={styles.listHeaderActions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              disabled={photoSyncing}
              onClick={() => refreshPhotosFromDrive(true)}
            >
              {photoSyncing ? 'Refreshing Photos...' : 'Refresh Photos'}
            </button>

            <input
              style={styles.searchInput}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, ID, or email..."
            />
          </div>
        </div>

        {filtered.length === 0 && (
          <div style={styles.muted}>No students found.</div>
        )}

        <div style={styles.studentList}>
          {filtered.map(student => {
            const isEditing = editing === student.id;
            return (
              <div key={student.id} style={styles.studentCard}>
                <div style={styles.studentCardInner}>
                  {/* Photo */}
                  <div style={styles.photoWrap}>
                    <div style={styles.photoPlaceholder}>
                      {photoLoading[student.student_code] ? (
                        <span style={styles.photoLoading}>↑</span>
                      ) : student.photo_drive_file_id &&
                        !brokenPhotos[student.student_code] ? (
                        <img
  src={getPhotoUrl(
    student.photo_drive_file_id,
    photoVersion
  )}
  alt={`${student.student_code} student`}
  style={styles.photoImage}
  loading="lazy"
  decoding="async"
  onError={() =>
    setBrokenPhotos(prev => ({
      ...prev,
      [student.student_code]: true
    }))
  }
/>
                      ) : (
                        <span style={styles.photoIcon}>👤</span>
                      )}
                    </div>
                    <label style={styles.photoUploadLabel}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => handlePhotoUpload(
                          student.student_code,
                          e.target.files[0]
                        )}
                      />
                      <span style={styles.photoUploadBtn}>Upload</span>
                    </label>
                  </div>

                  {/* Info */}
                  <div style={styles.studentInfo}>
                    {isEditing ? (
                      <div style={styles.editForm}>
                        <div style={styles.editRow}>
                          <div style={styles.editField}>
                            <label style={styles.label}>Chinese name</label>
                            <input
                              style={styles.input}
                              type="text"
                              defaultValue={student.chinese_name}
                              id={`chinese-${student.id}`}
                            />
                          </div>
                          <div style={styles.editField}>
                            <label style={styles.label}>English name</label>
                            <input
                              style={styles.input}
                              type="text"
                              defaultValue={student.english_name || ''}
                              id={`english-${student.id}`}
                            />
                          </div>
                          <div style={styles.editField}>
                            <label style={styles.label}>Student ID</label>
                            <input
                              style={styles.input}
                              type="text"
                              defaultValue={student.student_code}
                              id={`code-${student.id}`}
                            />
                          </div>
                          <div style={styles.editField}>
                            <label style={styles.label}>Email</label>
                            <input
                              style={styles.input}
                              type="email"
                              defaultValue={student.email || ''}
                              id={`email-${student.id}`}
                            />
                          </div>

                        </div>
                        <div style={styles.editActions}>
                          <button
                            style={styles.btn}
                            onClick={async () => {
  await handleUpdateStudent(student.id, {
    chinese_name: document.getElementById(`chinese-${student.id}`).value,
    english_name: document.getElementById(`english-${student.id}`).value || null,
    student_code: document.getElementById(`code-${student.id}`).value,
    email: document.getElementById(`email-${student.id}`).value || null
  });
  setEditing(null);
  setSuccess('Student updated.');
}}
                          >
                            Save
                          </button>
                          <button
                            style={styles.secondaryBtn}
                            onClick={() => setEditing(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={styles.studentNameRow}>
                          <div style={styles.studentName}>
                            {student.chinese_name ||
  student.english_name ||
  'Unnamed student'}

{student.chinese_name &&
  student.english_name && (
    <span
      style={styles.englishName}
    >
      {' / '}
      {student.english_name}
    </span>
  )}
                          </div>

                          
                        </div>
                        <div style={styles.studentMeta}>
                          {student.student_code}
                          {student.email && <span> · {student.email}</span>}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  {!isEditing && (
                    <div style={styles.studentActions}>
                      <button
                        style={styles.editBtn}
                        onClick={() => setEditing(student.id)}
                      >
                        Edit
                      </button>
                      {(session.isSuperadmin || student.added_by_instructor_id === session.instructorId) && (
  <button
    style={styles.dangerBtn}
    onClick={() => handleArchiveStudent(student.id, false)}
  >
    Archive
  </button>
)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Archived students */}
        {archivedStudents.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <button
              style={styles.secondaryBtn}
              onClick={() => setShowArchived(prev => !prev)}
            >
              {showArchived ? 'Hide' : 'Show'} archived students ({archivedStudents.length})
            </button>

            {showArchived && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {archivedStudents.map(student => (
                  <div key={student.id} style={{ ...styles.studentCard, opacity: 0.6 }}>
                    <div style={styles.studentCardInner}>
                      <div style={styles.studentInfo}>
                        <div style={styles.studentNameRow}>
                          <div style={styles.studentName}>
                            {student.chinese_name ||
  student.english_name ||
  'Unnamed student'}

{student.chinese_name &&
  student.english_name && (
    <span
      style={styles.englishName}
    >
      {' / '}
      {student.english_name}
    </span>
  )}
                          </div>

                          
                        </div>
                        <div style={styles.studentMeta}>{student.student_code}</div>
                      </div>
                      <div style={styles.studentActions}>
                        <button
                          style={styles.restoreBtn}
                          onClick={() => handleArchiveStudent(student.id, true)}
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  heading: { margin: '0 0 8px', fontSize: '24px', color: '#1f2933' },
  subheading: { margin: '0 0 16px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  card: {
    background: 'white', border: '1px solid #d7dee5', borderRadius: '12px',
    padding: '24px', marginBottom: '20px', boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
  },
  bulkGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px', marginBottom: '16px'
  },
  field: { marginBottom: '16px' },
  honorsBulkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '-2px 0 16px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933',
    cursor: 'pointer'
  },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  textarea: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box',
    background: 'white', fontFamily: 'monospace', resize: 'vertical'
  },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#66788a', border: '1px solid #d7dee5',
    borderRadius: '8px', padding: '9px 16px', fontSize: '14px', cursor: 'pointer'
  },
  listHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px', gap: '16px', flexWrap: 'wrap'
  },
  listHeaderActions: {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'
  },
  searchInput: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '8px 12px',
    fontSize: '14px', width: '260px', background: 'white'
  },
  studentList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  studentCard: {
    border: '1px solid #d7dee5', borderRadius: '10px',
    overflow: 'hidden', background: 'white'
  },
  studentCardInner: {
    display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '14px 16px'
  },
  photoWrap: {
    flexShrink: 0, display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '6px'
  },
  photoPlaceholder: {
    width: '56px', height: '56px', borderRadius: '8px',
    background: '#f0f4f7', border: '1px solid #d7dee5',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden'
  },
  photoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  photoIcon: { fontSize: '24px' },
  photoLoading: { fontSize: '18px', color: '#215b7d' },
  photoUploadLabel: { cursor: 'pointer' },
  photoUploadBtn: {
    fontSize: '11px', color: '#215b7d', fontWeight: '700',
    textDecoration: 'underline', cursor: 'pointer'
  },
  studentInfo: { flex: 1, minWidth: 0 },
  studentNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  studentName: { fontWeight: '700', fontSize: '15px', color: '#1f2933' },
  englishName: { fontWeight: '400', color: '#66788a' },
  honorsBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#eee8f8',
    color: '#5a4383',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '10px',
    fontWeight: '800',
    letterSpacing: '0.02em'
  },
  studentMeta: { fontSize: '13px', color: '#66788a', marginTop: '4px' },
  studentActions: { flexShrink: 0, display: 'flex', gap: '8px' },
  editBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  restoreBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  editForm: { width: '100%' },
  editRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  editField: { flex: 1, minWidth: '140px' },
  editHonorsField: {
    minWidth: '150px',
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: '9px'
  },
  editHonorsLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933',
    cursor: 'pointer'
  },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  editActions: { display: 'flex', gap: '8px' },
  masterBox: {
  background: '#f7f9fa', border: '1px solid #d7dee5', borderRadius: '10px',
  padding: '14px 16px', marginBottom: '20px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px'
},
masterText: { fontSize: '14px', color: '#1f2933' },
};